import React, { useContext, useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Select from "react-select";
import LeftSidePopupLayout from "../../../../../component/popup/modal-layout";
import XCloseIcon from "../../../../../component/icons/x-close";
import Style from "./style.module.css";
import { GlobalContext } from "../../../../../context/Context";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import { Loader2 } from "lucide-react";
import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
} from "../../../../../component/form/form-layout";

const LostDeal = ({
  onSubmit = () => {},
  onClose = () => {},
  onError = () => {},
  lostFormDeal,
}) => {
  const { state } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const [lostReasons, setLostReasons] = useState([]);
  const [isOther, setIsOther] = useState(false);
  const inputRef = useRef(null);

  // ✅ Fetch lost reasons
  useEffect(() => {
    const fetchLostReasons = async () => {
      try {
        const res = await apiCall({
          method: "get",
          url: "deals/get-deal-lost-reasons",
        });

        setLostReasons(
          (res?.data || []).map((item) => ({
            value: item.reason,
            label: item.reason,
          }))
        );
      } catch (err) {
        console.error("Lost reasons fetch error:", err);
        onError("Failed to load lost reasons.");
      }
    };

    fetchLostReasons();
  }, []);

  // ✅ Validation schema
  const validationSchema = yup.object({
    lost_reason: yup.mixed().required("This field is required"),
    lost_comment: yup.string(),
    custom_reason: yup.lazy((value, { parent }) => {
      if (parent?.lost_reason?.value === "Other") {
        return yup.string().required("Please provide a custom reason");
      }
      return yup.string();
    }),
  });

  const formik = useFormik({
    initialValues: {
      lost_reason: null,
      lost_comment: "",
      custom_reason: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // ✅ Determine final reason
        let finalReason =
          values.lost_reason?.value === "Other"
            ? values.custom_reason
            : values.lost_reason?.value;

        // ✅ Update deal status
        const data = {
          dealkey: lostFormDeal?.dealkey,
          dealstatus: "lost",
          lost_reason: finalReason,
          lost_comment: values.lost_comment,
        };

        const res = await apiCall({
          method: "post",
          url: "deals/update-deal-status",
          data,
        });

        formik.resetForm();
        onSubmit(res?.message);
      } catch (error) {
        console.error("Error updating deal:", error);
        onError(
          error?.response?.data?.message || "Failed to update deal status."
        );
      }
    },
  });

  // ✅ Handle “Other” selection
  useEffect(() => {
    const otherSelected = formik.values.lost_reason?.value === "Other";
    setIsOther(otherSelected);
    if (otherSelected) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [formik.values.lost_reason]);

  // ✅ Add "Other" option
  const options = [...lostReasons, { value: "Other", label: "Other" }];

  // ✅ react-select styles

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        {/* Header */}
        <div
          className={`${Style.modal_header} ${Style.addMeModal_head} flex justify-between items-center`}
        >
          <h4>Lost Deal</h4>
          <div
            onClick={onClose}
            className={`${Style.close_btn} cursor-pointer`}
          >
            <XCloseIcon />
          </div>
        </div>

        {/* Body */}
        <div className={`${Style.addMeModal_body}`}>
          <div className={Style.dcmb__card}>
            <div className={Style.dcmb__cardBody}>
              <div className={`${Style.dcmb__cardBody_formArea} pt-3`}>
                <FormArea>
                  {/* Lost Reason Dropdown */}
                  <FeildWraper width="100%">
                    <FormLabel required>Lost Reason</FormLabel>

                    <Select
                      name="lost_reason"
                      isMulti
                      className="text-sm"
                      classNamePrefix="react-select"
                      options={options}
                      components={{
                        IndicatorSeparator: () => null,
                        LoadingIndicator: () => null,
                      }}
                      value={options.filter((option) =>
                        Array.isArray(formik.values.lost_reason)
                          ? formik.values.lost_reason.includes(option.value)
                          : false
                      )}
                      onChange={(selectedOptions) => {
                        const ids = selectedOptions.map((opt) => opt.value);
                        formik.setFieldValue("lost_reason", ids);
                      }}
                      filterOption={(option, inputValue) =>
                        option.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                      placeholder="Select Reason..."
                      isClearable
                      menuPlacement="auto"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "35px",
                          height: "auto",
                          borderRadius: "5px",
                          border: "1px solid #DFDFDF",
                          backgroundColor: state.isDisabled
                            ? "#f0f0f0"
                            : "#FFF",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": { borderColor: "#DFDFDF" },
                          fontSize: "13px",
                          lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#1E1E1E",
                          padding: "0 14px",
                          cursor: state.isDisabled ? "not-allowed" : "normal", // cursor change
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          height: "auto",
                          padding: "0 0px 0",
                        }),
                        input: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                          fontSize: "14px",
                          lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#1E1E1E",
                          height: "auto",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "14px",
                          lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#1E1E1E",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          fontSize: "14px",
                          lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#717680",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 99999,
                        }),
                        menu: (base) => ({
                          ...base,
                          marginTop: "2px",
                          borderRadius: "5px",
                          border: "1px solid #DFDFDF",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: "14px",
                          lineHeight: "1.2",
                          fontWeight: 500,
                          color: state.isSelected ? "#FFF" : "#1E1E1E",
                          backgroundColor: state.isSelected
                            ? "#3b82f6"
                            : state.isFocused
                            ? "#F3F4F6"
                            : "#FFF",
                          padding: "8px 14px",
                          cursor: "pointer",
                        }),
                        indicatorContainer: (base) => ({ ...base, padding: 0 }),
                        dropdownIndicator: (base) => ({ ...base, padding: 0 }),
                      }}
                    />
                    {formik.touched.lost_reason &&
                      formik.errors.lost_reason && (
                        <FeildError>{formik.errors.lost_reason}</FeildError>
                      )}
                  </FeildWraper>

                  {/* Custom Reason Input (Only when "Other" selected) */}
                  {isOther && (
                    <FeildWraper width="100%">
                      <FormLabel required>Custom Reason</FormLabel>
                      <input
                        ref={inputRef}
                        type="text"
                        name="custom_reason"
                        placeholder="Enter your reason"
                        value={formik.values.custom_reason}
                        onChange={formik.handleChange}
                        className="border rounded-lg px-3 py-2 w-full"
                      />
                      {formik.touched.custom_reason &&
                        formik.errors.custom_reason && (
                          <FeildError>{formik.errors.custom_reason}</FeildError>
                        )}
                    </FeildWraper>
                  )}

                  <FeildWraper width="100%">
                    <FormLabel required>Comment</FormLabel>
                    <textarea
                      name="lost_comment"
                      rows={4}
                      placeholder="Add additional comments (optional)"
                      value={formik.values.lost_comment}
                      onChange={formik.handleChange}
                    ></textarea>
                  </FeildWraper>
                </FormArea>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={Style.dcmb__cardFoot}>
          <button type="reset" className={Style.dcmb__cancel} onClick={onClose}>
            Discard
          </button>
          <button type="submit" className={Style.dcmb__submit}>
            Apply
          </button>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default LostDeal;
